import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TrendingUp, TrendingDown, DollarSign, Package, ShoppingCart, AlertTriangle } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useState, useEffect } from 'react';
import { useCurrency } from '@/hooks/useCurrency';
import { formatPrice } from '@/utils/currency';

interface Order {
  id: string;
  status: string;
  amount_cents: number;
  currency: string;
  created_at: string;
}

interface Product {
  id: string;
  stock: number;
  active: boolean;
}

interface VendorStatsProps {
  orders: Order[];
  vendorIds?: string[];
}

export const VendorStats = ({ orders, vendorIds = [] }: VendorStatsProps) => {
  const { user } = useAuth();
  const { displayCurrency, convertPrice } = useCurrency();
  const [products, setProducts] = useState<Product[]>([]);
  const [transactions, setTransactions] = useState<Array<{ net_amount_cents: number; currency: string }>>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;
      const ids = vendorIds.length > 0 ? vendorIds : [user.id];
      
      try {
        // Fetch products for all accessible vendor stores
        const { data: productsData, error: productsError } = await supabase
          .from('products')
          .select('id, stock, active')
          .in('vendor_id', ids);
          
        if (productsError) throw productsError;
        setProducts(productsData || []);

        // Fetch real revenue from vendor_transactions (net amounts after commission)
        const { data: transactionsData, error: transactionsError } = await supabase
          .from('vendor_transactions')
          .select('net_amount_cents, currency')
          .in('vendor_id', ids)
          .eq('type', 'credit');
          
        if (transactionsError) throw transactionsError;
        
        setTransactions(transactionsData || []);
      } catch (error) {
        console.error('Error fetching vendor stats:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [user, vendorIds.join(',')]);

  // Calculate revenue with the shared app-wide currency conversion logic.
  const totalRevenue = useMemo(() => {
    const totalCents = transactions.reduce((sum, transaction) => {
      return sum + convertPrice(transaction.net_amount_cents, transaction.currency, displayCurrency);
    }, 0);

    return totalCents / 100;
  }, [transactions, displayCurrency, convertPrice]);

  const stats = useMemo(() => {
    // Produits actifs
    const activeProducts = products.filter(p => p.active).length;
    
    // Stock total
    const totalStock = products.reduce((sum, p) => sum + p.stock, 0);
    
    // Produits en rupture de stock (stock <= 5)
    const lowStockCount = products.filter(p => p.stock <= 5 && p.active).length;
    
    // Commandes à traiter
    const ordersToProcess = orders.filter(o => ['PAID', 'AWAITING_DROP_OFF'].includes(o.status)).length;

    return {
      activeProducts,
      totalStock,
      lowStockCount,
      ordersToProcess,
      totalOrders: orders.length
    };
  }, [orders, products]);

  const StatCard = ({ title, value, icon: Icon, subtitle, variant = 'default' }: any) => (
    <Card className="border-gray-200 shadow-sm">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 p-3 md:p-4">
        <CardTitle className="text-[10px] md:text-sm font-medium text-gray-600 truncate pr-1">{title}</CardTitle>
        <Icon className={`h-3.5 w-3.5 md:h-4 md:w-4 flex-shrink-0 ${variant === 'warning' ? 'text-orange-500' : 'text-gray-400'}`} />
      </CardHeader>
      <CardContent className="p-3 md:p-4 pt-0">
        <div className={`text-base md:text-2xl font-bold truncate ${variant === 'warning' ? 'text-orange-600' : ''}`}>
          {value}
        </div>
        {subtitle && (
          <p className="text-[10px] md:text-xs text-muted-foreground mt-1 truncate">{subtitle}</p>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        {[1, 2, 3, 4].map(i => (
          <Card key={i} className="border-gray-200 shadow-sm animate-pulse">
            <CardContent className="p-3 md:p-4">
              <div className="h-12 bg-gray-200 rounded"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
      <StatCard
        title="Products"
        value={stats.activeProducts}
        icon={Package}
        subtitle={`Total stock: ${stats.totalStock} units`}
      />
      <StatCard
        title="Orders"
        value={stats.totalOrders}
        icon={ShoppingCart}
        subtitle={`${stats.ordersToProcess} to process`}
      />
      <StatCard
        title="Revenue"
        value={formatPrice(Math.round(totalRevenue * 100), displayCurrency)}
        icon={DollarSign}
        subtitle="Total sales"
      />
      <StatCard
        title="Low Stock"
        value={stats.lowStockCount}
        icon={AlertTriangle}
        subtitle="products out of stock"
        variant={stats.lowStockCount > 0 ? 'warning' : 'default'}
      />
    </div>
  );
};
